import { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, Package, DollarSign, MessageCircle, HelpCircle, AlertCircle, CheckCircle2, RotateCcw, Clock, Eye, Heart } from 'lucide-react';
import { toast } from 'sonner';

interface Product {
  id: string;
  name: string;
  price: number;
  stock?: number;
  isArchived?: boolean;
  views?: number;
  favorites?: number;
  createdAt?: any;
}

interface AnalyticsData {
  name: string;
  quantitySold: number;
  revenue: number;
  views: number;
  favorites: number;
}

export function VendorAnalytics({ vendorId }: { vendorId: string }) {
  const [data, setData] = useState<AnalyticsData[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Stats
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalItemsSold, setTotalItemsSold] = useState(0);
  const [unreadChats, setUnreadChats] = useState(0);
  const [unansweredQA, setUnansweredQA] = useState(0);
  const [pendingFulfillment, setPendingFulfillment] = useState(0);

  // Universal Chart configuration
  const [chartType, setChartType] = useState<'bar' | 'line' | 'pie'>('bar');
  const [chartMetric1, setChartMetric1] = useState<keyof AnalyticsData>('quantitySold');
  const [chartMetric2, setChartMetric2] = useState<keyof AnalyticsData | 'none'>('revenue');

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#64748b', '#14b8a6', '#f97316', '#6366f1'];

  const PLATFORM_FEE_PERCENT = 0.035;

  const fetchAnalytics = async () => {
    if (!vendorId || !auth.currentUser) return;
    setLoading(true);
    try {
      // 1. Fetch Products
      const productsQuery = query(collection(db, 'products'), where('vendorId', '==', vendorId));
      const productsSnap = await getDocs(productsQuery);
      const fetchedProducts: Product[] = [];
      const itemStats: Record<string, AnalyticsData> = {};
      
      productsSnap.forEach(doc => {
        const p = { id: doc.id, ...doc.data() } as Product;
        fetchedProducts.push(p);
        itemStats[p.id] = {
           name: p.name || 'Unknown Item',
           quantitySold: 0,
           revenue: 0,
           views: p.views || 0,
           favorites: p.favorites || 0
        };
      });
      setProducts(fetchedProducts);

      // 2. Fetch Transactions (for sales and unfulfilled orders)
      const txQuery = query(collection(db, 'transactions'), where('vendorIds', 'array-contains', vendorId));
      const txSnap = await getDocs(txQuery);
      
      let revenueSum = 0;
      let itemsSum = 0;
      let pendingCount = 0;

      txSnap.docs.forEach(doc => {
        const tx = doc.data();
        if (tx.status === 'completed') {
           if (!tx.fulfillmentStatus || tx.fulfillmentStatus === 'pending' || tx.fulfillmentStatus === 'preparing') {
             pendingCount++;
           }
           if (tx.items) {
             tx.items.forEach((item: any) => {
               if (item.vendorId === vendorId) {
                 if (itemStats[item.id]) {
                    itemStats[item.id].quantitySold += item.quantity;
                    itemStats[item.id].revenue += (item.price * item.quantity) / 100;
                 }
                 revenueSum += (item.price * item.quantity) / 100;
                 itemsSum += item.quantity;
               }
             });
           }
        }
      });
      
      setPendingFulfillment(pendingCount);
      setTotalRevenue(revenueSum * (1 - PLATFORM_FEE_PERCENT));
      setTotalItemsSold(itemsSum);

      // 3. Fetch Unread Chats
      const chatsQuery = query(collection(db, 'chat_threads'), where('vendorId', '==', vendorId));
      const chatsSnap = await getDocs(chatsQuery);
      let unread = 0;
      chatsSnap.forEach(doc => {
         const thread = doc.data();
         if (thread.readStatus && !thread.readStatus[auth.currentUser!.uid]) {
            unread++;
         }
      });
      setUnreadChats(unread);

      // 4. Fetch Unanswered Q&As
      const qaQuery = query(collection(db, 'product_qa'), where('vendorId', '==', vendorId));
      const qaSnap = await getDocs(qaQuery);
      let unanswered = 0;
      qaSnap.forEach(doc => {
         if (!doc.data().answer) unanswered++;
      });
      setUnansweredQA(unanswered);

      // Build final chart data by sorting best performers
      const sortedData = Object.values(itemStats)
        .sort((a, b) => b.views - a.views) // Sort by views or sales
        .slice(0, 10);
      setData(sortedData);

    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [vendorId]);

  const handleBumpProduct = async (productId: string) => {
    try {
      await updateDoc(doc(db, 'products', productId), {
        createdAt: serverTimestamp()
      });
      toast.success('Listing bumped to the top!');
      fetchAnalytics(); // Refresh
    } catch (err) {
      toast.error('Failed to bump listing.');
    }
  };

  const activeProducts = products.filter(p => !p.isArchived && (p.stock === undefined || p.stock > 0));
  const outOfStockProducts = products.filter(p => p.stock !== undefined && p.stock <= 0);
  const archivedProducts = products.filter(p => p.isArchived);
  const totalViews = products.reduce((sum, p) => sum + (p.views || 0), 0);
  const totalFavorites = products.reduce((sum, p) => sum + (p.favorites || 0), 0);

  // Top overall performer
  const topPerformer = [...data].sort((a, b) => b.quantitySold - a.quantitySold)[0];

  if (loading) {
     return <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">Loading insights...</div>;
  }

  return (
    <div className="space-y-6">
      
      {/* 1. Actionable Overview */}
      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm p-6">
         <h2 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">Action Needed Overview</h2>
         <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900/50 p-4 rounded-xl flex items-center justify-between">
               <div className="flex items-center gap-3">
                  <div className="bg-amber-100 dark:bg-amber-900/40 p-2 rounded-lg text-amber-600 dark:text-amber-400">
                     <MessageCircle className="w-5 h-5" />
                  </div>
                  <div>
                     <p className="text-sm font-medium text-amber-900 dark:text-amber-100">Chats to answer</p>
                     <p className="text-2xl font-semibold text-amber-700 dark:text-amber-400">{unreadChats}</p>
                  </div>
               </div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/50 p-4 rounded-xl flex items-center justify-between">
               <div className="flex items-center gap-3">
                  <div className="bg-blue-100 dark:bg-blue-900/40 p-2 rounded-lg text-blue-600 dark:text-blue-400">
                     <HelpCircle className="w-5 h-5" />
                  </div>
                  <div>
                     <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Questions to answer</p>
                     <p className="text-2xl font-semibold text-blue-700 dark:text-blue-400">{unansweredQA}</p>
                  </div>
               </div>
            </div>
            <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-900/50 p-4 rounded-xl flex items-center justify-between">
               <div className="flex items-center gap-3">
                  <div className="bg-indigo-100 dark:bg-indigo-900/40 p-2 rounded-lg text-indigo-600 dark:text-indigo-400">
                     <Package className="w-5 h-5" />
                  </div>
                  <div>
                     <p className="text-sm font-medium text-indigo-900 dark:text-indigo-100">Pending Fulfillment</p>
                     <p className="text-2xl font-semibold text-indigo-700 dark:text-indigo-400">{pendingFulfillment}</p>
                  </div>
               </div>
            </div>
         </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
         {/* 2. Listing Health & Summary Stats */}
         <div className="lg:col-span-1 space-y-6">
            <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm p-6 overflow-hidden relative">
               <div className="absolute -right-6 -top-6 text-neutral-100 dark:text-neutral-800 pointer-events-none">
                  <TrendingUp className="w-32 h-32 opacity-50" />
               </div>
               <h2 className="text-lg font-medium text-neutral-900 dark:text-white mb-4 relative z-10">Sales Overview</h2>
               <div className="space-y-4 relative z-10">
                  <div>
                     <p className="text-sm text-neutral-500 dark:text-neutral-400">Total Items Sold</p>
                     <p className="text-3xl font-serif font-medium text-neutral-900 dark:text-white">{totalItemsSold}</p>
                  </div>
                  <div>
                     <p className="text-sm text-neutral-500 dark:text-neutral-400">Total Net Revenue</p>
                     <p className="text-3xl font-serif font-medium text-neutral-900 dark:text-white">${totalRevenue.toFixed(2)}</p>
                     <p className="text-[10px] text-neutral-400 mt-1 italic">After 5% platform fee</p>
                  </div>
               </div>
            </div>

            <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm p-6">
               <h2 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">Your Listings</h2>
               <div className="grid grid-cols-2 gap-3">
                  <div className="border border-neutral-200 dark:border-neutral-800 rounded-xl p-3 bg-neutral-50 dark:bg-neutral-800/50">
                     <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-500 mb-1">
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="text-xs font-semibold uppercase tracking-wider">Active</span>
                     </div>
                     <p className="text-xl font-medium text-neutral-900 dark:text-white">{activeProducts.length}</p>
                  </div>
                  <div className="border border-neutral-200 dark:border-neutral-800 rounded-xl p-3 bg-neutral-50 dark:bg-neutral-800/50">
                     <div className="flex items-center gap-1.5 text-red-600 dark:text-red-500 mb-1">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-xs font-semibold uppercase tracking-wider">Out of Stock</span>
                     </div>
                     <p className="text-xl font-medium text-neutral-900 dark:text-white">{outOfStockProducts.length}</p>
                  </div>
                  <div className="border border-neutral-200 dark:border-neutral-800 rounded-xl p-3 bg-neutral-50 dark:bg-neutral-800/50">
                     <div className="flex items-center gap-1.5 text-neutral-500 mb-1">
                        <Clock className="w-4 h-4" />
                        <span className="text-xs font-semibold uppercase tracking-wider">Drafts / Hidden</span>
                     </div>
                     <p className="text-xl font-medium text-neutral-900 dark:text-white">{archivedProducts.length}</p>
                  </div>
                  <div className="border border-neutral-200 dark:border-neutral-800 rounded-xl p-3 bg-indigo-50 dark:bg-indigo-900/20">
                     <div className="flex items-center gap-1.5 text-indigo-600 dark:text-indigo-400 mb-1">
                        <RotateCcw className="w-4 h-4" />
                        <span className="text-xs font-semibold uppercase tracking-wider">Needs Bump</span>
                     </div>
                     <p className="text-xl font-medium text-neutral-900 dark:text-white">{products.length}</p>
                  </div>
               </div>
            </div>
         </div>

         {/* 3. Universal Charts Dashboard */}
         <div className="lg:col-span-2">
            <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm h-full flex flex-col">
               <div className="p-6 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50 flex flex-wrap md:flex-nowrap items-center justify-between gap-4">
                  <div>
                     <h2 className="text-xl font-medium text-neutral-900 dark:text-white flex items-center gap-2 font-serif">
                     <TrendingUp className="w-5 h-5 text-indigo-500 dark:text-indigo-400 stroke-[1.5]" />
                     Marketplace Insights
                     </h2>
                     <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Compare views, favorites, and sales.</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                     <select value={chartMetric1} onChange={(e) => setChartMetric1(e.target.value as any)} className="text-xs font-medium rounded-md px-2 py-1.5 border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300">
                        <option value="views">Views</option>
                        <option value="favorites">Favorites</option>
                        <option value="quantitySold">Sales</option>
                        <option value="revenue">Revenue</option>
                     </select>
                     <span className="text-neutral-400 text-xs">vs</span>
                     <select value={chartMetric2} onChange={(e) => setChartMetric2(e.target.value as any)} className="text-xs font-medium rounded-md px-2 py-1.5 border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300">
                        <option value="none">None</option>
                        <option value="views">Views</option>
                        <option value="favorites">Favorites</option>
                        <option value="quantitySold">Sales</option>
                        <option value="revenue">Revenue</option>
                     </select>
                     <div className="h-6 w-px bg-neutral-200 dark:bg-neutral-700 mx-1"></div>
                     <select value={chartType} onChange={(e) => setChartType(e.target.value as any)} className="text-xs font-medium rounded-md px-2 py-1.5 border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300">
                        <option value="bar">Bar Chart</option>
                        <option value="line">Line Chart</option>
                        <option value="pie">Pie Chart</option>
                     </select>
                  </div>
               </div>

               <div className="grid grid-cols-3 border-b border-neutral-100 dark:border-neutral-800 divide-x divide-neutral-100 dark:divide-neutral-800">
                  <div className="p-4 bg-white dark:bg-neutral-900 flex items-center justify-center gap-3">
                     <Eye className="w-5 h-5 text-neutral-400" />
                     <div><p className="text-xs text-neutral-500 uppercase tracking-wider">Total Views</p><p className="font-semibold text-lg">{totalViews}</p></div>
                  </div>
                  <div className="p-4 bg-white dark:bg-neutral-900 flex items-center justify-center gap-3">
                     <Heart className="w-5 h-5 text-neutral-400" />
                     <div><p className="text-xs text-neutral-500 uppercase tracking-wider">Total Favorites</p><p className="font-semibold text-lg">{totalFavorites}</p></div>
                  </div>
                  <div className="p-4 bg-white dark:bg-neutral-900 flex items-center justify-center gap-3">
                     <Package className="w-5 h-5 text-neutral-400" />
                     <div><p className="text-xs text-neutral-500 uppercase tracking-wider">Items in Catalog</p><p className="font-semibold text-lg">{products.length}</p></div>
                  </div>
               </div>
               
               <div className="flex-1 p-6 min-h-[400px]">
                  {data.length === 0 ? (
                     <div className="h-full flex flex-col items-center justify-center text-neutral-400">
                        <TrendingUp className="w-12 h-12 mb-3 stroke-[1]" />
                        <p>No product data available to chart.</p>
                     </div>
                  ) : chartType === 'pie' ? (
                     <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                           <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e5e5e5' }} />
                           <Legend />
                           <Pie data={data} dataKey={chartMetric1} nameKey="name" cx="50%" cy="50%" outerRadius={120} label>
                              {data.map((entry, index) => (
                                 <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                           </Pie>
                        </PieChart>
                     </ResponsiveContainer>
                  ) : chartType === 'line' ? (
                     <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                           <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e5e5" />
                           <XAxis dataKey="name" tick={{ fill: '#737373', fontSize: 12 }} tickFormatter={(val) => val.length > 10 ? val.substring(0, 10)+'...' : val} />
                           <YAxis yAxisId="left" tick={{ fill: '#737373', fontSize: 12 }} />
                           <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e5e5e5' }} />
                           <Legend />
                           <Line yAxisId="left" type="monotone" dataKey={chartMetric1} stroke="#3b82f6" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                           {chartMetric2 !== 'none' && (
                              <Line yAxisId="left" type="monotone" dataKey={chartMetric2} stroke="#10b981" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                           )}
                        </LineChart>
                     </ResponsiveContainer>
                  ) : (
                     <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                           <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e5e5" />
                           <XAxis dataKey="name" tick={{ fill: '#737373', fontSize: 12 }} tickFormatter={(val) => val.length > 10 ? val.substring(0, 10)+'...' : val} />
                           <YAxis yAxisId="left" tick={{ fill: '#737373', fontSize: 12 }} />
                           <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e5e5e5' }} cursor={{ fill: 'rgba(0,0,0,0.05)' }} />
                           <Legend />
                           <Bar yAxisId="left" dataKey={chartMetric1} fill="#3b82f6" radius={[4, 4, 0, 0]} />
                           {chartMetric2 !== 'none' && (
                              <Bar yAxisId="left" dataKey={chartMetric2} fill="#10b981" radius={[4, 4, 0, 0]} />
                           )}
                        </BarChart>
                     </ResponsiveContainer>
                  )}
               </div>
            </div>
         </div>
      </div>

      {/* 4. Top Performing Content */}
      {topPerformer && topPerformer.quantitySold > 0 && (
         <div className="bg-indigo-900 text-white rounded-2xl overflow-hidden shadow-sm p-8 relative">
            <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
               <TrendingUp className="w-48 h-48" />
            </div>
            <h2 className="text-xl font-medium mb-6 font-serif flex items-center gap-2 relative z-10">
               Top Performing Item
            </h2>
            <div className="flex items-center gap-6 relative z-10">
               <div className="bg-indigo-800/50 p-6 rounded-xl border border-indigo-700 flex-1">
                  <h3 className="text-2xl font-semibold mb-2">{topPerformer.name}</h3>
                  <div className="flex gap-8">
                     <div>
                        <p className="text-indigo-300 text-xs uppercase tracking-wider mb-1">Sales</p>
                        <p className="text-3xl font-serif">{topPerformer.quantitySold}</p>
                     </div>
                     <div>
                        <p className="text-indigo-300 text-xs uppercase tracking-wider mb-1">Revenue</p>
                        <p className="text-3xl font-serif">${topPerformer.revenue.toFixed(2)}</p>
                     </div>
                     <div>
                        <p className="text-indigo-300 text-xs uppercase tracking-wider mb-1">Views</p>
                        <p className="text-3xl font-serif">{topPerformer.views}</p>
                     </div>
                  </div>
               </div>
               <div className="hidden sm:block">
                  <div className="w-32 h-32 bg-indigo-800 rounded-xl flex items-center justify-center">
                     <Package className="w-12 h-12 text-indigo-400" />
                  </div>
               </div>
            </div>
         </div>
      )}

    </div>
  );
}
